import {createSelector} from 'reselect';

import {AppParametricSelector} from '../../helpers/reselectHelpers';
import {ConversationModel} from '../../models/api/conversationModel';
import {OnboardingTipTypesEnum} from '../../models/api/preferencesModel';
import {getIsInTrialOrManagedTrial} from '../data/company/companySelectors';
import {getConversations} from '../data/dataSelectors';
import {
  getGuidedDemoConversationId,
  getSeenOnboardingTips,
  getWasOnboardingCompleted,
} from '../data/preferences/preferencesSelectors';
import {getIsUserFirstGlobalAdmin, getUser} from '../data/teammates/teammatesSelectors';
import {getHasActionCtasBundle, getHasGuidedDemoFlag} from '../featureFlags/featureFlagsSelectors';

export const getIsGuidedDemoActive = createSelector(
  [
    getIsInTrialOrManagedTrial,
    getIsUserFirstGlobalAdmin,
    getHasGuidedDemoFlag,
    getSeenOnboardingTips,
    getConversations,
    getGuidedDemoConversationId,
  ],
  (
    isInTrialOrManagedTrial,
    isUserFirstGlobalAdmin,
    hasGuidedDemoFlag,
    seenOnboardingTips,
    conversations,
    guidedDemoConversationId,
  ) => {
    // The prerequisites for the guided demo being active are:
    // 1. The user is in trial, the first global admin, and has the feature flag.
    // 2. The guided demo end bubble has not been seen (otherwise the tour is already over).
    const hasPrerequisites =
      isInTrialOrManagedTrial &&
      isUserFirstGlobalAdmin &&
      hasGuidedDemoFlag &&
      !seenOnboardingTips.includes(OnboardingTipTypesEnum.GUIDED_DEMO_END);

    // Return early if the prerequisites don't qualify.
    if (!hasPrerequisites) {
      return false;
    }

    // If the user has all the prerequisites, they demo is active if we have a demo conversation OR there
    // is no demo conversation but the user has seen the composer send bubble. This covers the two phases of
    // the tour: one where the demo conversation exists, the other where the demo assets have been deleted.
    return (
      Boolean(guidedDemoConversationId && conversations[guidedDemoConversationId]) ||
      seenOnboardingTips.includes(OnboardingTipTypesEnum.GUIDED_DEMO_COMPOSER_SEND)
    );
  },
);

const teammateTourMinimumUserCreationDate = 1591912800000; // June 11, 2020 3PM PST.

export const getIsTeammateTourActive = createSelector(
  [getIsUserFirstGlobalAdmin, getUser, getSeenOnboardingTips, getWasOnboardingCompleted],
  (isUserFirstGlobalAdmin, user, seenOnboardingTips, wasOnboardingCompleted) =>
    // The teammate tour is active if:
    // 1. The user is not a global admin, and has the feature flag.
    // 2. The conversation view bubble has not been seen (otherwise the tour is already over).
    !isUserFirstGlobalAdmin &&
    user.createdAt > teammateTourMinimumUserCreationDate &&
    !seenOnboardingTips.includes(OnboardingTipTypesEnum.COMMENT_COMPOSER) &&
    wasOnboardingCompleted,
);

type GetConversationSelector = AppParametricSelector<any, ConversationModel | undefined>;
export type GetIsPersonalizeDemoInboxActiveSelector = AppParametricSelector<any, boolean>;
export function makeGetIsPersonalizedDemoInboxActive(getConversation: GetConversationSelector) {
  return createSelector(
    [getHasActionCtasBundle, getConversation, getIsUserFirstGlobalAdmin],
    (hasActionCtasBundle, conversation, isUserFirstGlobalAdmin) =>
      // Demo inbox is active if: 1. we have the proper feature flag 2. the current conversation is the demo conv.
      // 3. user is the first global admin.
      Boolean(hasActionCtasBundle && conversation && conversation.isGuidedDemo && isUserFirstGlobalAdmin),
  );
}
